import { useMemo } from 'react';
import { bool, func } from 'prop-types';
import styled from 'styled-components';
import { Link } from "react-router-dom";
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DefaultButton from '../../StoraEnsoComponents/Button';
import DataTable from '../../DataTable';
import { sortAlphanumericFunc, sortByDateTimeFunc } from '../../DataTable/utils';
import { ReactComponent as WarningSvg } from '../../../assets/icons/fas-triangle-exclamation.svg';


export const WarningIcon = styled(WarningSvg)`
  width: 16px;
  height: auto;
  fill: ${({theme}) => theme.colors.attention};
`;


const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const IconWrapper = styled.div`
  margin-right: 8px;
  color: #67b419;
`;

const Button = styled(DefaultButton)`
  padding: 0 12px;

  span {
    padding: 4px 0;
  }
`;

const getTypeStr = type => {
  switch(type) {
    case 'single':
      return 'Photo';
    case 'panorama':
      return 'Panorama';
    case 'video':
      return 'Video';
    case 'forest':
    case 'forestMulti':
    case 'forestRGB':
      return 'Forest';
    case 'mill':
      return 'Mill';
    default:
      return '-';
  }
}

const getIconStr = type => {
  switch(type) {
    case 'panorama':
      return 'street-view';
    case 'video':
      return 'video';
    case 'forest':
    case 'forestMulti':
    case 'forestRGB':
        return 'tree';
    case 'mill':
      return 'industry';
    default:
      return 'images';
  }
}

const getColumns = ({onItemSelect, onItemZoom}) => [
  {
    id: 'name',
    Header: 'Name',
    accessor: 'name',
    sortType: sortAlphanumericFunc,
    Cell: ({
      value,
      row: { original: { id, } }
    }) => (
      <Link to={`/project/detail/${id}`}>{value || '-'}</Link>
    )
  },
  {
    id: 'type',
    Header: 'Type',
    accessor: 'type',
    customHeaderWidth: 150,
    Cell: ({ value }) => (
      <Wrapper>
        <IconWrapper>
          <FontAwesomeIcon icon={['fal', getIconStr(value)]} />
        </IconWrapper>
        <span className="type">{getTypeStr(value)}</span>
      </Wrapper>
    ),
  },
  {
    id: 'statusDescription',
    Header: 'Status',
    accessor: 'statusDescription',
    customHeaderWidth: 100,
    Cell: ({
      value,
      row: { original: { processingError} }
    }) => {
      return (
        <>
          {processingError ? <WarningIcon /> : <>{value || '-'}</>}
        </>
    )}
  },
  {
    id: 'location',
    Header: 'Location',
    accessor: 'location',
    Cell: ({ value }) => (<div>{value || '-'}</div>),
  },
  {
    id: 'created',
    Header: 'Created',
    accessor: 'created',
    sortType: sortByDateTimeFunc,
    customHeaderWidth: 150,
    Cell: ({ value }) => !value ? '' : DateTime.fromISO(value).toLocaleString({dateStyle: 'short', hour12: false}),
  },
  {
    Header: () => null,
    accessor: 'none',
    disableSortBy: true,
    customAlignment: 'right',
    Cell: ({
      row: { original: { id, hasGeometry } }
    }) => {
      return (
      <>
        <Button kind="attention" disabled={!hasGeometry} onClick={() => onItemZoom(id)} size="sm">Zoom</Button>
        <Button onClick={() => onItemSelect(id)} size="sm">Detail</Button>
      </>
    )}
  },
];

function MediaGrid({ data, loading, onItemSelect, onItemZoom }) {
  const columns = useMemo(() => getColumns({onItemSelect, onItemZoom}), [onItemSelect, onItemZoom]);

  return (
    <DataTable
      gridId="mediaTable"
      minWidth="850px"
      columns={columns}
      data={data}
      loading={loading}
      initialSortBy={{
        id: 'created',
        desc: true,
      }}
      tableStyle={{
        removeWrapperMargin: true,
        isDense: true,
      }}
    />
  );
}

MediaGrid.propTypes = {
  loading: bool.isRequired,
  onItemSelect: func.isRequired,
  onItemZoom: func.isRequired,
}

export default MediaGrid;